/**
 * PB Pick & Mix — Think Pink (Boobie Bars) tooltip
 *
 * The pinned Boobie Bar card has a note ("Telt mee voor je korting · zelf geen
 * korting") that explains why the card has no Pick & Mix discount. The note is
 * a button that opens a tooltip:
 *
 * - mouse: opens on hover, closes on leave (unless opened with a click)
 * - keyboard: opens on focus, Enter/Space toggle, Escape closes
 * - touch: a tap toggles it, a tap elsewhere closes it
 *
 * Also the buy block's two-photo carousel ([data-pm-tp-carousel]): dots below
 * the photo, arrows on desktop, swipe on touch (CSS scroll-snap), and back to
 * the first photo when a colour is picked, because that is the photo the colour
 * choice swaps.
 *
 * Basket behaviour (counts for the tier, no discount of its own) lives in the
 * section's own script.
 */
(function () {
  const TIP_SELECTOR = '[data-pm-tp-tip]';
  const TRIGGER_SELECTOR = '[data-pm-tp-tip-trigger]';
  const OPEN_CLASS = 'is-open';

  function getTrigger(tip) {
    return tip.querySelector(TRIGGER_SELECTOR);
  }

  function setOpen(tip, open) {
    tip.classList.toggle(OPEN_CLASS, open);
    getTrigger(tip)?.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (!open) delete tip.dataset.pmTpPinned;
  }

  function closeAll(except) {
    for (const tip of document.querySelectorAll(`${TIP_SELECTOR}.${OPEN_CLASS}`)) {
      if (tip !== except) setOpen(tip, false);
    }
  }

  function bind(tip) {
    if (tip.dataset.pmTpBound === 'true') return;
    tip.dataset.pmTpBound = 'true';

    const trigger = getTrigger(tip);
    if (!trigger) return;

    tip.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      closeAll(tip);
      setOpen(tip, true);
    });

    tip.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse' || tip.dataset.pmTpPinned === 'true') return;
      setOpen(tip, false);
    });

    trigger.addEventListener('focus', () => {
      closeAll(tip);
      setOpen(tip, true);
    });

    trigger.addEventListener('blur', () => {
      setOpen(tip, false);
    });

    // Click / tap / Enter / Space. A click keeps the tooltip open after the
    // mouse leaves, a second click closes it again.
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      const pinned = tip.dataset.pmTpPinned === 'true';

      closeAll(tip);
      setOpen(tip, !pinned);
      if (!pinned) tip.dataset.pmTpPinned = 'true';
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !tip.classList.contains(OPEN_CLASS)) return;
      event.stopPropagation();
      setOpen(tip, false);
    });
  }

  const CAROUSEL_SELECTOR = '[data-pm-tp-carousel]';

  function bindCarousel(carousel) {
    if (carousel.dataset.pmTpCarouselBound === 'true') return;
    carousel.dataset.pmTpCarouselBound = 'true';

    const track = carousel.querySelector('[data-pm-tp-slides]');
    const dots = Array.from(carousel.querySelectorAll('[data-pm-tp-dot]'));
    const arrows = Array.from(carousel.querySelectorAll('[data-pm-tp-arrow]'));
    if (!track || !dots.length) return;

    let current = 0;

    const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function setActive(index) {
      current = index;
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      // No arrow towards a photo that is not there.
      arrows.forEach((arrow) => {
        const step = Number(arrow.dataset.pmTpArrow) || 0;
        const target = index + step;
        arrow.hidden = target < 0 || target > dots.length - 1;
      });
    }

    function goTo(index) {
      track.scrollTo({ left: index * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
      setActive(index);
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => goTo(index));
    });

    arrows.forEach((arrow) => {
      arrow.addEventListener('click', () => {
        const step = Number(arrow.dataset.pmTpArrow) || 0;
        goTo(Math.max(0, Math.min(dots.length - 1, current + step)));
        // The clicked arrow can disappear at the end; keep keyboard focus.
        if (arrow.hidden && document.activeElement === arrow) {
          arrows.find((other) => !other.hidden)?.focus({ preventScroll: true });
        }
      });
    });

    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const width = track.clientWidth || 1;
          setActive(Math.min(dots.length - 1, Math.round(track.scrollLeft / width)));
        });
      },
      { passive: true },
    );

    // A colour choice swaps the first photo: show it.
    const card = carousel.closest('[data-pm-card]');
    card?.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-pm-variant-swatch]')) {
        goTo(0);
      }
    });
  }

  function init() {
    document.querySelectorAll(TIP_SELECTOR).forEach(bind);
    document.querySelectorAll(CAROUSEL_SELECTOR).forEach(bindCarousel);
  }

  document.addEventListener('pointerdown', (event) => {
    if (!(event.target instanceof Element) || event.target.closest(TIP_SELECTOR)) return;
    closeAll(null);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
